/**
 * One provider's card on Settings → Calendar — V6-07 §4.
 *
 * Three states, and each one has to be honest about something people would
 * otherwise discover the hard way:
 *
 *  - **Unavailable.** The platform has registered no OAuth app for this
 *    provider. Shown rather than hidden, because a missing button reads as a
 *    missing feature and generates a support message either way — this one at
 *    least says who can fix it.
 *  - **Not connected.** The Connect button, and the overwrite rule stated
 *    *before* consent rather than in a guide afterwards.
 *  - **Connected.** Which account, the calendar picker, a link to the
 *    VirtueTasks calendar, the health line in plain words, and Disconnect with
 *    the delete-the-calendar offer §1 requires.
 */

import { useState } from 'react';
import type { CalendarProvider } from '@virtuetasks/types';
import { AlertTriangle, CalendarCheck, ExternalLink, Loader2, RefreshCw, Unplug } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { describeError } from '@/lib/errorMessage';
import {
  useDisconnectCalendar,
  useSelectCalendars,
  useStartCalendarConnect,
  useSyncCalendarNow,
  type CalendarConnectionView,
} from '@/features/calendar/api';

const LABELS: Record<CalendarProvider, string> = {
  google: 'Google Calendar',
  microsoft: 'Microsoft 365 / Outlook',
};

const TONE_COLOUR: Record<'ok' | 'warn' | 'error', string> = {
  ok: 'var(--ok, var(--accent))',
  warn: 'var(--warn)',
  error: 'var(--danger)',
};

export function CalendarConnectionCard({
  provider,
  configured,
  connection,
}: {
  provider: CalendarProvider;
  configured: boolean;
  connection?: CalendarConnectionView;
}) {
  const start = useStartCalendarConnect();
  const select = useSelectCalendars();
  const syncNow = useSyncCalendarNow();
  const disconnect = useDisconnectCalendar();
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [alsoDelete, setAlsoDelete] = useState(false);

  const label = LABELS[provider];

  async function connect(): Promise<void> {
    setBusy(true);
    try {
      const { url } = await start.mutateAsync(provider);
      // A full navigation, not a popup: the consent screen refuses to render
      // in an iframe and a blocked popup looks like a broken button.
      window.location.href = url;
    } catch (err) {
      setBusy(false);
      toast.error(describeError(err, `Could not start the ${label} connection`), {
        duration: 15_000,
      });
    }
  }

  function toggleCalendar(calendarId: string): void {
    if (!connection) return;
    const selected = connection.calendars
      .filter((calendar) =>
        calendar.calendarId === calendarId ? !calendar.selected : calendar.selected,
      )
      .map((calendar) => calendar.calendarId);
    select
      .mutateAsync({ provider, selected })
      .catch((err: unknown) => toast.error(describeError(err, 'Could not save your choice')));
  }

  /**
   * §1 makes deleting the calendar an *offer*, not a policy — it is full of
   * their tasks with dates they may still want. So the choice is a checkbox in
   * front of them rather than a second confirm dialog they will click through.
   */
  async function remove(): Promise<void> {
    try {
      const result = await disconnect.mutateAsync({ provider, deleteCalendar: alsoDelete });
      setConfirming(false);
      toast.success(
        result.deletedCalendar
          ? `${label} disconnected and the VirtueTasks calendar was deleted.`
          : `${label} disconnected. The VirtueTasks calendar was left in place.`,
      );
    } catch (err) {
      toast.error(describeError(err, 'Could not disconnect'), { duration: 15_000 });
    }
  }

  return (
    <section className="vt-surface p-4" data-testid={`calendar-${provider}-card`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 text-[14px] font-medium">
            <CalendarCheck className="size-4" />
            {label}
          </h2>
          <p className="mt-1 max-w-lg text-[12px] vt-muted">
            Two-way. Your assigned tasks and personal to-dos with due dates go into a calendar
            named “VirtueTasks” that we create; meetings from the calendars you pick come back into
            VirtueTasks to read.
          </p>
        </div>
        {connection ? (
          <Badge tone={connection.status === 'connected' ? 'accent' : 'warn'}>
            {connection.status === 'connected' ? 'Connected' : 'Reconnect needed'}
          </Badge>
        ) : (
          <Badge tone="neutral">{configured ? 'Not connected' : 'Unavailable'}</Badge>
        )}
      </div>

      {!configured && !connection && (
        <p
          className="mt-3 flex items-start gap-1.5 text-[12px] text-[var(--warn)]"
          data-testid={`calendar-${provider}-unavailable`}
        >
          <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
          {label} sync has not been set up on this platform yet. Ask a platform administrator to
          register the app in the platform console.
        </p>
      )}

      {!connection && configured && (
        <div className="mt-4 flex flex-col gap-2">
          {/* Said before consent, not discovered afterwards (§1). */}
          <p className="text-[12px] vt-muted" data-testid={`calendar-${provider}-overwrite-note`}>
            <strong className="text-[var(--text)]">VirtueTasks owns what it puts there.</strong> If
            you rename or delete an event in the VirtueTasks calendar, it is put back. The one
            change that sticks is moving it to another day — that becomes the task’s new due date.
          </p>
          <Button
            className="self-start"
            data-testid={`calendar-${provider}-connect`}
            disabled={busy || start.isPending}
            onClick={() => void connect()}
          >
            {busy || start.isPending ? <Loader2 className="size-4 animate-spin" /> : null}
            Connect {label}
          </Button>
        </div>
      )}

      {connection && (
        <div className="mt-4 flex flex-col gap-3">
          <p className="text-[12px]" data-testid={`calendar-${provider}-account`}>
            Connected as <span className="vt-mono">{connection.accountEmail}</span>
          </p>

          <p
            className="text-[12px]"
            data-testid={`calendar-${provider}-health`}
            style={{ color: TONE_COLOUR[connection.health.tone] }}
          >
            {connection.health.summary}
          </p>

          {/**
           * V8-04 §2 — what actually came in.
           *
           * "Synced 2 min ago" reads the same whether the sync returned a full
           * diary or nothing, and "connected but empty" was the founder's whole
           * report. This is the line that tells the two apart.
           */}
          {connection.inbound && (
            <p className="text-[12px] vt-muted" data-testid={`calendar-${provider}-inbound`}>
              {connection.inbound}
            </p>
          )}

          <div className="flex flex-wrap items-center gap-2">
            {connection.calendarUrl && (
              <a
                className="inline-flex items-center gap-1 text-[12px] underline"
                href={connection.calendarUrl}
                target="_blank"
                rel="noreferrer"
                data-testid={`calendar-${provider}-open`}
              >
                <ExternalLink className="size-3.5" />
                Open my VirtueTasks calendar
              </a>
            )}
            <Button
              variant="ghost"
              size="sm"
              data-testid={`calendar-${provider}-sync`}
              disabled={syncNow.isPending}
              onClick={() =>
                syncNow
                  .mutateAsync(provider)
                  .then((result) =>
                    result.outcome.ran
                      ? toast.success('Synced')
                      : toast.error(result.outcome.detail, { duration: 15_000 }),
                  )
                  .catch((err: unknown) => toast.error(describeError(err, 'Sync failed')))
              }
            >
              {syncNow.isPending ? (
                <Loader2 className="size-3.5 animate-spin" />
              ) : (
                <RefreshCw className="size-3.5" />
              )}
              Sync now
            </Button>
            <Button
              variant="ghost"
              size="sm"
              data-testid={`calendar-${provider}-disconnect`}
              disabled={disconnect.isPending}
              onClick={() => setConfirming(true)}
            >
              <Unplug className="size-3.5" />
              Disconnect
            </Button>
          </div>

          {confirming && (
            <div
              className="flex flex-col gap-2 rounded-[8px] border border-[var(--warn)]/40 p-3"
              data-testid={`calendar-${provider}-confirm`}
            >
              <p className="text-[12px]">
                Disconnecting deletes the stored access immediately. Meetings pulled in from this
                account are removed from VirtueTasks.
              </p>
              <label className="flex items-center gap-2 text-[12px]">
                <input
                  type="checkbox"
                  checked={alsoDelete}
                  data-testid={`calendar-${provider}-delete-calendar`}
                  onChange={(e) => setAlsoDelete(e.target.checked)}
                />
                Also delete the “VirtueTasks” calendar from {label}
              </label>
              <div className="flex gap-2">
                <Button
                  variant="danger"
                  size="sm"
                  data-testid={`calendar-${provider}-disconnect-confirm`}
                  disabled={disconnect.isPending}
                  onClick={() => void remove()}
                >
                  {disconnect.isPending ? <Loader2 className="size-3.5 animate-spin" /> : null}
                  Disconnect
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setConfirming(false)}>
                  Keep it connected
                </Button>
              </div>
            </div>
          )}

          <div data-testid={`calendar-${provider}-picker`}>
            <h3 className="text-[12px] font-medium">Which calendars to show in VirtueTasks</h3>
            <p className="mt-0.5 text-[11px] vt-muted">
              Read-only. VirtueTasks never changes, moves or deletes anything in these.
            </p>
            <ul className="mt-1.5 flex flex-col gap-1">
              {connection.calendars.map((calendar) => (
                <li key={calendar.calendarId}>
                  <label className="flex items-center gap-2 text-[12px]">
                    <input
                      type="checkbox"
                      checked={calendar.selected}
                      data-testid={`calendar-${provider}-calendar-${calendar.calendarId}`}
                      onChange={() => toggleCalendar(calendar.calendarId)}
                    />
                    <span>{calendar.name}</span>
                    {calendar.isPrimary && <Badge tone="neutral">primary</Badge>}
                    {calendar.kind && <Badge tone="neutral">{calendar.kind}</Badge>}
                  </label>
                </li>
              ))}
              {connection.calendars.length === 0 && (
                <li className="text-[12px] vt-muted">This account has no other calendars.</li>
              )}
            </ul>
          </div>
        </div>
      )}
    </section>
  );
}
